import { Alert, Button, message, Popconfirm, Space } from 'antd'
import {
  useGeckoPermissions,
  useRolodexPermissionsAllUserMetadata,
} from 'features/permissions'
import {
  ApprovalNotificationContext,
  buildAssignmentNotifications,
  buildSignedNotification,
  buildVoidedNotifications,
} from 'pages/reporting/shared/approval/approvalNotifications'
import {
  ApprovalWorkflowDraft,
  canEditAssignments,
  getSignatureForRole,
  getSignBlocker,
  SIGNOFF_ROLE_LABELS,
} from 'pages/reporting/shared/approval/approvalRules'
import { ApprovalRoleStatusRow } from 'pages/reporting/shared/approval/components/ApprovalRoleStatusRow'
import {
  ApprovalWorkflowEditor,
  AssigneeOption,
} from 'pages/reporting/shared/approval/components/ApprovalWorkflowEditor'
import { useApprovalNotifications } from 'pages/reporting/shared/approval/useApprovalNotifications'
import { useApprovalWorkflow } from 'pages/reporting/shared/approval/useApprovalWorkflow'
import { ESignatureDialog } from 'pages/reporting/shared/eSignature/components/ESignatureDialog'
import { UseReportESignaturesResult } from 'pages/reporting/shared/eSignature/useReportESignatures'
import { useSignedPdfDownload } from 'pages/reporting/shared/eSignature/useSignedPdfDownload'
import { SignRequest } from 'pages/reporting/shared/eSignature/utils/signReport'
import { FC, useMemo, useState } from 'react'
import {
  PERMISSION_CANTILEVER_PUBLISHER_ADMIN,
  ReportApprovalWorkflow,
  SignoffRole,
  SignoffState,
} from 'types'

const draftFromWorkflow = (
  workflow: ReportApprovalWorkflow | undefined
): ApprovalWorkflowDraft => ({
  requiredRoles: workflow?.requiredRoles ?? [
    SignoffRole.SUPERVISOR,
    SignoffRole.REVIEWER,
  ],
  assignments: { ...workflow?.assignments },
})

const showError = (fallback: string) => (error: unknown) =>
  message.error(error instanceof Error ? error.message : fallback)

const buildSigningLink = (reviewSectionId: string) => {
  const url = new URL(window.location.href)
  url.searchParams.set('section', reviewSectionId)
  return url.toString()
}

interface ReportApprovalPanelProps {
  metadataEntryId: string
  workflow: ReportApprovalWorkflow | undefined
  signoff: SignoffState | undefined
  signatures: UseReportESignaturesResult
  qualificationOptions: readonly string[]
  reviewSectionId: string
  reportTitle: string
  organizationId: string | undefined
  readOnly?: boolean
  onWorkflowSaved: (next: {
    approvalWorkflow: ReportApprovalWorkflow
    signoff: SignoffState
  }) => void
}

/**
 * Configure who must sign a report, then collect their electronic signatures
 * on the latest draft PDF. Only the assigned account can sign each role.
 */
export const ReportApprovalPanel: FC<ReportApprovalPanelProps> = ({
  metadataEntryId,
  workflow,
  signoff,
  signatures,
  qualificationOptions,
  reviewSectionId,
  reportTitle,
  organizationId,
  readOnly = false,
  onWorkflowSaved,
}) => {
  const { hasPermissions } = useGeckoPermissions()
  const { currentUser, canManage, isSaving, saveWorkflow } =
    useApprovalWorkflow({
      metadataEntryId,
      workflow,
      signoff,
      onSaved: onWorkflowSaved,
    })
  const downloadSignedPdf = useSignedPdfDownload()
  const sendNotifications = useApprovalNotifications()
  const notificationContext: ApprovalNotificationContext | undefined =
    currentUser && organizationId
      ? {
          reportTitle,
          organizationId,
          reviewUrl: buildSigningLink(reviewSectionId),
          actor: currentUser,
        }
      : undefined
  const { data: users } = useRolodexPermissionsAllUserMetadata()
  const assigneeOptions = useMemo<AssigneeOption[]>(
    () =>
      (users ?? [])
        .filter((user) => user.active && user.name && user.email)
        .map((user) => ({
          value: user.id,
          label: `${user.name} (${user.email})`,
          ref: { userId: user.id, name: user.name, email: user.email },
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  )

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState<ApprovalWorkflowDraft>(() =>
    draftFromWorkflow(workflow)
  )
  const [signingRole, setSigningRole] = useState<SignoffRole>()

  const { records } = signatures
  const showEditor = !readOnly && (isEditing || (!workflow && canManage))
  const canVoid =
    !readOnly &&
    records.length > 0 &&
    (canManage ||
      hasPermissions([PERMISSION_CANTILEVER_PUBLISHER_ADMIN]) ||
      records.some((record) => record.signerId === currentUser?.userId))

  const handleStartEditing = () => {
    setDraft(draftFromWorkflow(workflow))
    setIsEditing(true)
  }

  const handleSave = () =>
    saveWorkflow(draft)
      .then((saved) => {
        setIsEditing(false)
        message.success('Approval roles saved')
        if (notificationContext) {
          void sendNotifications(
            buildAssignmentNotifications(workflow, saved, notificationContext)
          )
        }
      })
      .catch(showError('Failed to save approval roles'))

  const handleCopySigningLink = () =>
    navigator.clipboard
      .writeText(buildSigningLink(reviewSectionId))
      .then(() => message.success('Signing link copied'))
      .catch(showError('Failed to copy signing link'))

  const handleDownloadSignedPdf = () =>
    downloadSignedPdf(metadataEntryId, records, 'client')

  const handleDownloadAuditCopy = () =>
    downloadSignedPdf(metadataEntryId, records, 'audit')

  const handleVoid = () => {
    const voided = records
    return signatures
      .voidSignatures()
      .then(() => {
        message.success('Signatures voided')
        if (notificationContext && workflow) {
          void sendNotifications(
            buildVoidedNotifications(workflow, voided, notificationContext)
          )
        }
      })
      .catch(showError('Failed to void signatures'))
  }

  const handleSign = (request: SignRequest) =>
    signatures.sign(request).then((record) => {
      setSigningRole(undefined)
      message.success('Report signed')
      const notification =
        notificationContext &&
        workflow &&
        buildSignedNotification(
          workflow,
          [...records, record],
          request.role,
          notificationContext
        )
      if (notification) void sendNotifications([notification])
    })

  if (showEditor) {
    return (
      <ApprovalWorkflowEditor
        draft={draft}
        assigneeOptions={assigneeOptions}
        qualificationOptions={qualificationOptions}
        isSaving={isSaving}
        canCancel={workflow !== undefined}
        onChange={setDraft}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
      />
    )
  }

  if (!workflow) {
    return (
      <Alert
        type='info'
        showIcon
        message='Approval roles have not been configured for this report yet.'
      />
    )
  }

  return (
    <Space orientation='vertical' size='middle' style={{ width: '100%' }}>
      {!signatures.hasDraft && !readOnly && (
        <Alert
          type='warning'
          showIcon
          message='Generate a draft PDF before collecting signatures.'
        />
      )}
      {records.length > 0 && !signatures.isSignedDocumentCurrent && (
        <Alert
          type='error'
          showIcon
          message='A newer draft was generated after signing. Void the signatures and sign the current draft before publishing.'
        />
      )}

      <div>
        {workflow.requiredRoles.map((role) => (
          <ApprovalRoleStatusRow
            key={role}
            label={SIGNOFF_ROLE_LABELS[role]}
            assigneeName={workflow.assignments[role]?.name ?? 'Unassigned'}
            qualifications={workflow.assignments[role]?.qualifications}
            signedAt={getSignatureForRole(records, role)?.signedAt}
            readOnly={readOnly}
            isBusy={signatures.isBusy}
            signDisabledReason={
              getSignBlocker(workflow, records, role, currentUser) ??
              (signatures.hasDraft
                ? undefined
                : 'Generate a draft PDF before signing')
            }
            onSign={() => setSigningRole(role)}
          />
        ))}
      </div>

      <Space wrap>
        {!readOnly && canManage && (
          <Button
            disabled={!canEditAssignments(records) || signatures.isBusy}
            title={
              canEditAssignments(records)
                ? undefined
                : 'Void the signatures to change who must approve'
            }
            onClick={handleStartEditing}>
            Edit approval roles
          </Button>
        )}
        {!readOnly && (
          <Button onClick={handleCopySigningLink}>Copy signing link</Button>
        )}
        {records.length > 0 && (
          <>
            <Button
              disabled={signatures.isBusy}
              title='The signed report as it will be published, without the certificate page'
              onClick={handleDownloadSignedPdf}>
              Download signed PDF
            </Button>
            <Button
              disabled={signatures.isBusy}
              title='Internal copy with the signature certificate page for audit'
              onClick={handleDownloadAuditCopy}>
              Download audit copy
            </Button>
          </>
        )}
        {canVoid && (
          <Popconfirm
            title='Void all signatures?'
            description='Signed PDFs are deleted and every signer must sign again.'
            okText='Void signatures'
            okButtonProps={{ danger: true }}
            onConfirm={handleVoid}>
            <Button danger disabled={signatures.isBusy}>
              Void signatures
            </Button>
          </Popconfirm>
        )}
      </Space>

      {signingRole && currentUser && (
        <ESignatureDialog
          role={signingRole}
          signerName={currentUser.name}
          defaultQualifications={
            workflow.assignments[signingRole]?.qualifications
          }
          qualificationOptions={qualificationOptions}
          onCancel={() => setSigningRole(undefined)}
          onSign={handleSign}
        />
      )}
    </Space>
  )
}
